<script setup lang="ts">
import { ref, watch } from "vue";
import { onClickOutside } from "@vueuse/core";
import CanvasButton from "@/components/canvas/CanvasButton.vue";
import { toolbarLayoutKey } from "@/stores/CanvasLayoutState";
import { isCanvasDrawing } from "@/stores/CanvasViewState";
import { toolbarPopperOptions } from "./toolbarPopper";

/**
 * Bouton de la barre avec une flèche dans le coin (maquettes V5) : le bouton
 * bascule l'aide, le coin ouvre ses options à droite de la barre. Clic droit
 * sur le bouton : mêmes options, à la souris seulement (au doigt, l'appui
 * long déclenche aussi `contextmenu` : il est réservé à l'infobulle).
 */
withDefaults(
  defineProps<{
    phosphorIcon: string;
    /** Aide allumée : un mode, pas un outil sélectionné (cf. CanvasButton). */
    modeOn: boolean;
    tooltipTitle: string;
    tooltipText: string;
    /** Nom du menu d'options, pour la flèche du coin. */
    menuLabel: string;
    menuWidth?: number;
  }>(),
  { menuWidth: 200 },
);

const emit = defineEmits<{ (e: "toggle"): void }>();

const tool = ref<HTMLElement>();
const corner = ref<HTMLElement>();
const menu = ref<HTMLElement>();
const isMenuOpen = ref(false);

function toggleMenu() {
  isMenuOpen.value = !isMenuOpen.value;
}

function closeMenu() {
  isMenuOpen.value = false;
}

let lastPointerType = "";

function rememberPointerType(event: PointerEvent) {
  lastPointerType = event.pointerType;
}

function onContextMenu() {
  if (lastPointerType === "mouse") toggleMenu();
}

onClickOutside(menu, closeMenu, { ignore: [corner] });

// La barre change de forme : le menu ne suivrait pas son outil, on le ferme.
watch(toolbarLayoutKey, closeMenu);

const popperOptions = toolbarPopperOptions(() => tool.value, 10);

defineExpose({ closeMenu });
</script>

<template>
  <div
    ref="tool"
    class="corner-menu-tool"
    :class="{ 'mode-on': modeOn }"
    @pointerdown.capture="rememberPointerType"
    @contextmenu.prevent="onContextMenu"
  >
    <CanvasButton
      :phosphor-icon="phosphorIcon"
      :mode="modeOn"
      :tooltip-title="tooltipTitle"
      :tooltip-text="tooltipText"
      @click="emit('toggle')"
    />
    <button
      ref="corner"
      type="button"
      class="corner-menu-trigger"
      :aria-label="menuLabel"
      :aria-expanded="isMenuOpen"
      aria-haspopup="menu"
      @click.stop="toggleMenu"
    >
      <span class="corner-caret"></span>
    </button>
    <slot name="badge"></slot>

    <el-popover
      :visible="isMenuOpen"
      :virtual-ref="tool"
      virtual-triggering
      placement="right-start"
      :width="menuWidth"
      :show-arrow="false"
      :popper-options="popperOptions"
      :popper-class="`canvas-popper snap-popper${isCanvasDrawing ? ' drawing-active' : ''}`"
    >
      <div ref="menu">
        <slot :close="closeMenu"></slot>
      </div>
    </el-popover>
  </div>
</template>

<style lang="scss" scoped>
.corner-menu-tool {
  position: relative;
  width: 36px;
  height: 36px;
}

// Zone tactile du coin bas droit : un quart du bouton, assez pour le doigt.
.corner-menu-trigger {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  border-radius: 0 0 8px 0;
  background: transparent;
  color: #fff;
  cursor: pointer;
  z-index: 1;

  .corner-menu-tool.mode-on & {
    color: var(--canvas-btn-active-bg);
  }
}

.corner-caret {
  position: absolute;
  right: 3px;
  bottom: 3px;
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-bottom: 5px solid currentColor;
  opacity: 0.7;
}
</style>
